import { CurrentAddresses } from "../CurrentAddress/CurrentAddress"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import { MovingApplications } from "../MovingApplications/MovingApplications"
import { SMOOTH_SMOOTHER_API_URL, V1_OPE_URL } from "src/lib/constants/value"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { formatDate } from "src/components/List/List"
import * as styles from "./Proposal.css"
import { Proposal as ProposalType } from "src/hooks/useProposalSWR"
import useSWR, { mutate } from "swr"
import { Contract } from "../Contract/Contract"
import { SimplifiedExaminations } from "../SimplifiedExaminations/SimplifiedExaminations"
import { useForm } from "react-hook-form"
import { TextAreaField } from "src/components/TextAreaField"
import { useEffect, useMemo } from "react"
import { color, GrayRoundInput } from "@smoothincorg/smooth-ui"
import { BorderedButton } from "src/components/Button"
import { useEditingId } from "../../hooks/useEditingId"
import { StoreSelector } from "src/app/_components/StoreSelector"
import Link from "next/link"
import { useNotification } from "@smoothincorg/smooth-ui"
import { SearchHouses } from "../SearchHouses"
import Image from "next/image"
import { StoreInList } from "src/hooks/useStoresSWR"

export const INTRODUCTION_STATUSES = [
  { id: 1, content: "enquete", content_jp: "初回アンケート回答済み" },
  { id: 2, content: "first_message", content_jp: "初回メッセージ回答済み" },
  {
    id: 4,
    content: "house_information_registered",
    content_jp: "物件情報登録済み",
  },
  {
    id: 8,
    content: "house_information_url_registered",
    content_jp: "希望物件URL登録済み",
  },
  {
    id: 16,
    content: "simplified_examination_applied",
    content_jp: "事前審査申込済み",
  },
  { id: 32, content: "inbound_from_store", content_jp: "店舗利用モデルである" },
  { id: 64, content: null, content_jp: null },
  { id: 128, content: null, content_jp: null },
  { id: 256, content: null, content_jp: null },
  { id: 512, content: null, content_jp: null },
  { id: 1024, content: null, content_jp: null },
  {
    id: 2048,
    content: "simplified_examination_ok",
    content_jp: "事前審査合格",
  },
  {
    id: 4096,
    content: "simplified_examination_ng",
    content_jp: "事前審査不合格",
  },
  { id: 8192, content: null, content_jp: null },
  { id: 16384, content: "visit", content_jp: "部屋の探し方で来店を選択済み" },
  {
    id: 32768,
    content: "online",
    content_jp: "部屋の探し方でオンラインを選択済み",
  },
  { id: 65536, content: null, content_jp: null },
  { id: 131072, content: null, content_jp: null },
  {
    id: 262144,
    content: "sent_search_house_link",
    content_jp: "希望物件情報へのLIFFリンクを送付した",
  },
  {
    id: 524288,
    content: "no_bypass",
    content_jp: "ブツなしなしユーザーである",
  },
  {
    id: 1048576,
    content: "simplified_examination_via_chat",
    content_jp: "LINEで審査情報回収テスト対象ユーザー",
  },
  {
    id: 2097152,
    content: "welfare",
    content_jp: "福利厚生プラン",
  },
]

const SEARCH_STATUSES = [
  { id: 1, content: "visit_store", content_jp: "来店希望" },
  { id: 2, content: "visit_house", content_jp: "内見希望" },
  { id: 4, content: "online_chat", content_jp: "オンラインチャット提案希望" },
  { id: 8, content: "online_tel", content_jp: "オンライン電話提案希望" },
  { id: 16, content: "propose_property", content_jp: "物件提案フラグ" },
  {
    id: 32,
    content: "store_line_group_in",
    content_jp: "加盟店とLINEグループ作成済み",
  },
  {
    id: 64,
    content: "store_email_group_in",
    content_jp: "加盟店とメールにて送客済み",
  },
  {
    id: 128,
    content: "visit_store_schedule_applied",
    content_jp: "来店希望日程送信済",
  },
  {
    id: 256,
    content: "visit_store_schedule_fixed",
    content_jp: "来店希望日程を仲介事業者が承認済",
  },
  { id: 512, content: "visited_store", content_jp: "仲介事業者へ来店済み" },
  {
    id: 1024,
    content: "identification_document_uploaded",
    content_jp: "本人確認書類アップロード済み",
  },
  {
    id: 2048,
    content: "income_document_uploaded",
    content_jp: "収入証明書類アップロード済み",
  },
  {
    id: 4096,
    content: "has_valid_moving_application",
    content_jp: "有効な入居申し込みを持つ",
  },
  { id: 8192, content: null, content_jp: null },
  { id: 16384, content: null, content_jp: null },
  { id: 32768, content: null, content_jp: null },
  { id: 65536, content: null, content_jp: null },
  {
    id: 131072,
    content: "sorry_far_from_home",
    content_jp: "遠い店舗を提案する",
  },
]

const StoreName: React.FC<{ storeId: number }> = ({ storeId }) => {
  const { getWithToken } = useSmoothApi()

  const { data } = useSWR(
    storeId ? `${SMOOTH_SMOOTHER_API_URL}/stores/${storeId}` : null,
    async (url) => {
      const response = await getWithToken(url)
      return response.data["store"] as StoreInList
    }
  )

  return <>{data?.name}</>
}

type ProposalProps = {
  proposal: ProposalType
  mutateProposals: () => Promise<void>
}

export const StatusItems = ({
  targetStatus,
  statuses,
}: {
  targetStatus: number
  statuses: {
    id: number
    content: string
    content_jp: string
  }[]
}) => (
  <div className="statuses">
    {statuses.map((status) => {
      if (targetStatus & status.id) {
        return (
          <div key={status.id} style={{ fontSize: 12 }}>
            {status.content_jp}
          </div>
        )
      }
    })}
  </div>
)

export const StatusEditor: React.FC<
  {
    statuses: {
      id: number
      content: string
      content_jp: string
    }[]
    value: number
    onChange: React.ChangeEventHandler<HTMLInputElement>
    label: React.ReactNode
  } & Omit<React.ComponentProps<"div">, "onChange">
> = ({ ...props }) => {
  const checkedLabelStyle = {
    border: "2px solid " + color.main,
    fontWeight: "bold",
  }

  return (
    <div {...props}>
      <span
        color="darkBlue"
        style={{
          display: "block",
          fontSize: 14,
          marginBottom: 4,
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          display: "block",
          marginBottom: 8,
          color: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <GrayRoundInput disabled value={props.value} />
      </span>

      {props.statuses.map((status) => {
        const checked = props.value & status.id
        return (
          <label
            key={status.id}
            style={{
              display: "block",
              border: "2px solid " + color.mediumLightGray,
              padding: 5,
              marginBottom: 8,
              borderRadius: 5,
              fontSize: 12,
              cursor: "pointer",
              width: "60%",
              overflow: "scroll",
              ...(checked ? checkedLabelStyle : {}),
            }}
          >
            <input
              type="checkbox"
              name="searchStatus"
              value={status.id}
              onChange={props.onChange}
              style={{ display: "none" }}
            />

            <div style={{ display: "flex", height: "100%", alignItems: "center" }}>
              {`${status.id}: ${status.content_jp}`}
              {checked ? (
                <Image
                  src="/images/checked.svg"
                  alt=""
                  width={20}
                  height={20}
                  style={{
                    width: 20,
                    height: 20,
                    display: "inline-block",
                    marginLeft: "auto",
                    marginRight: 0,
                  }}
                />
              ) : null}
            </div>
          </label>
        )
      })}
      <input type="hidden" />
    </div>
  )
}

const deleteProposal = async (
  id: number,
  userId: number,
  fetcher: ReturnType<typeof useSmoothApi>["deleteWithToken"]
) => {
  await fetcher(`${SMOOTH_SMOOTHER_API_URL}/users/${userId}/proposals/${id}`)
}

export const Proposal: React.FC<ProposalProps> = ({ proposal, ...props }) => {
  const { register, setValue, watch, reset, handleSubmit } = useForm({
    defaultValues: {
      searchStatus: "",
      introductionStatus: "",
      memo: "",
      storeId: 0,
    },
  })

  const proposalResourceName = `Proposal`
  const { postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const { editingId, setEditingId } = useEditingId(proposalResourceName)
  const { showAlert, showSnackbar } = useNotification()

  useEffect(() => {
    if (proposal) {
      reset({
        searchStatus: `${proposal.searchStatus}`,
        introductionStatus: `${proposal.introductionStatus}`,
        memo: proposal.memo,
        storeId: proposal.storeId,
      })
    }

    if (editingId === 0) {
      reset({
        searchStatus: "0",
        introductionStatus: "0",
        memo: "",
        storeId: null,
      })
    }
  }, [editingId, proposal, reset])

  const searchStatus = watch("searchStatus")
  const introductionStatus = watch("introductionStatus")
  const storeId = watch("storeId")

  const hasStore = storeId > 0

  const scheduleTypesToCreate = useMemo<("email_grouped" | "line_grouped")[]>(() => {
    const previousSearchStatus = proposal.searchStatus
    const nextSearchStatus = parseInt(searchStatus)

    if (!hasStore) {
      return []
    }

    const types: ("email_grouped" | "line_grouped")[] = []

    if ((previousSearchStatus & 32) === 0 && (nextSearchStatus & 32) > 0) {
      types.push("line_grouped")
    }

    if ((previousSearchStatus & 64) === 0 && (nextSearchStatus & 64) > 0) {
      types.push("email_grouped")
    }

    return types
  }, [hasStore, proposal.searchStatus, searchStatus])

  const shouldSubmitSchedule = scheduleTypesToCreate.length > 0

  const submitSchedule = async () => {
    const jstCurrent = new Date(Date.now() + (new Date().getTimezoneOffset() + 9 * 60) * 60 * 1000)

    await postWithToken(`${SMOOTH_SMOOTHER_API_URL}/proposals/${proposal.id}/schedules`, {
      schedule: {
        proposalId: proposal.id,
        user_id: proposal.userId,
        store_id: proposal.storeId,
        schedule_type: scheduleTypesToCreate[0],
        scheduled_at: jstCurrent.toISOString(),
        status: "fixed",
        is_done: true,
      },
    })
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <ModalEditableForm
        resourceName={"Proposal"}
        resource={proposal}
        opeHref={`${V1_OPE_URL}/admin/proposals/${proposal.id}`}
        items={[
          [
            "Search Status",
            <StatusItems
              key={proposal.searchStatus}
              targetStatus={proposal.searchStatus}
              statuses={SEARCH_STATUSES}
            />,
          ],
          [
            "Introduction Status",
            <StatusItems
              key={proposal.introductionStatus}
              targetStatus={proposal.introductionStatus}
              statuses={INTRODUCTION_STATUSES}
            />,
          ],
          ["店舗名", <StoreName key={proposal.storeId} storeId={proposal.storeId} />],
          [
            "書類",
            <Link
              key={proposal.id}
              href={`${V1_OPE_URL}/admin/proposals/${proposal.id}/edit_documents`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                color: color.main,
                textDecoration: "underline",
              }}
            >
              書類を確認する
            </Link>,
          ],
          [
            "ファイル",
            <Link
              key={proposal.id}
              href={`/admin/document/${proposal.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                color: color.main,
                textDecoration: "underline",
              }}
            >
              チャットで送付されたファイルを確認する
            </Link>,
          ],
          [
            "契約準備ページ",
            <Link
              key={proposal.id}
              href={`/admin/lifftter/${proposal.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                color: color.main,
                textDecoration: "underline",
              }}
            >
              契約準備ページを開く
            </Link>,
          ],
          ["createdAt", formatDate(proposal.createdAt)],
          ["updatedAt", formatDate(proposal.updatedAt)],
        ]}
        onSubmit={handleSubmit(async (data) => {
          if (shouldSubmitSchedule) {
            try {
              await submitSchedule()
            } catch (e) {
              showSnackbar("スケジュールの作成に失敗しました", "error")
              return
            }
          }

          try {
            if (editingId === 0) {
              const response = await postWithToken(
                `${SMOOTH_SMOOTHER_API_URL}/users/${proposal.userId}/proposals`,
                {
                  proposal: {
                    search_status: parseInt(data.searchStatus),
                    introduction_status: parseInt(data.introductionStatus),
                    memo: data.memo,
                    store_id: data.storeId,
                  },
                }
              )

              mutate(`${SMOOTH_SMOOTHER_API_URL}/users/${proposal.userId}/proposals`)

              setEditingId(response.data["data"]["id"])
              showSnackbar("作成しました", "success")
              return
            }

            await patchWithToken(
              `${SMOOTH_SMOOTHER_API_URL}/users/${proposal.userId}/proposals/${editingId}`,
              {
                proposal: {
                  search_status: parseInt(data.searchStatus),
                  introduction_status: parseInt(data.introductionStatus),
                  memo: data.memo,
                  store_id: data.storeId,
                },
              }
            )

            mutate(`${SMOOTH_SMOOTHER_API_URL}/users/${proposal.userId}/proposals`)

            showSnackbar("更新しました", "success")
          } catch (e) {
            showSnackbar("更新に失敗しました", "error")
          }
        })}
        onDelete={async () => {
          const result = window.confirm("Proposalを削除します。よろしいですか？")

          if (!result) return

          const prompted = window.prompt("確認のため削除するProposalのIDを入力してください")

          if (prompted !== `${proposal.id}`) {
            showAlert("IDが一致しませんでした")
            return
          }

          deleteProposal(proposal.id, proposal.userId, deleteWithToken).then(() => {
            showSnackbar("削除しました", "success")
            props.mutateProposals()
          })
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginBottom: 20,
          }}
        >
          <span
            style={{
              fontSize: 14,
              marginBottom: 10,
            }}
          >
            店舗
          </span>
          <StoreSelector
            value={storeId}
            onChange={(value) => {
              setValue("storeId", value)
            }}
          />
        </div>
        <StatusEditor
          label={
            <span>
              Search Status
              {scheduleTypesToCreate.length > 0 && (
                <span style={{ color: "red", marginLeft: 10 }}>
                  Proposal保存時にスケジュールを作成します
                </span>
              )}
            </span>
          }
          statuses={SEARCH_STATUSES}
          style={{ marginBottom: 30 }}
          onChange={(e) => {
            const changedStatus = parseInt(e.target.value)
            const hasStatusAlready = parseInt(searchStatus) & changedStatus

            const newStatus = hasStatusAlready
              ? parseInt(searchStatus) - changedStatus
              : parseInt(searchStatus) + changedStatus

            setValue("searchStatus", `${newStatus}`)
          }}
          value={parseInt(searchStatus)}
        />
        <StatusEditor
          label="Introduction Status"
          statuses={INTRODUCTION_STATUSES}
          style={{ marginBottom: 30 }}
          onChange={(e) => {
            const currentStatus = parseInt(introductionStatus)
            const changedStatus = parseInt(e.target.value)
            const hasStatusAlready = currentStatus & changedStatus

            const newStatus = hasStatusAlready
              ? currentStatus - changedStatus
              : currentStatus + changedStatus

            setValue("introductionStatus", `${newStatus}`)
          }}
          value={parseInt(introductionStatus)}
        />
        <TextAreaField
          name="memo"
          label="メモ"
          {...register("memo")}
          defaultValue={proposal.memo}
        />
        <BorderedButton
          type="submit"
          style={{
            margin: "20px 0",
            width: "100%",
            height: 50,
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          保存
        </BorderedButton>
      </ModalEditableForm>
      <div className={styles.movingApplications}>
        <SimplifiedExaminations proposalId={proposal.id} limit={3} />
      </div>
      <div className={styles.movingApplications}>
        <SearchHouses proposalId={proposal.id} limit={3} />
      </div>
      <div className={styles.movingApplications}>
        <MovingApplications proposalId={proposal.id} />
      </div>
      <div className={styles.movingApplications}>
        <CurrentAddresses proposalId={proposal.id} />
      </div>
      <div className={styles.movingApplications}>
        <Contract proposalId={proposal.id} />
      </div>
    </div>
  )
}
